import { isEqual } from 'lodash-es'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

import { RoomSortOption } from '@/entities/room/@x/map'

import { TSearchFilter } from '../../model/schema'

type SearchFilterAction = {
  _updateCount: (filters: Partial<TSearchFilter>) => number
  update: (filters: Partial<TSearchFilter>) => void
  updateKeyword: (keyword: string) => void
  updateSort: (sort: RoomSortOption) => void
  reset: (fields: Field | Field[]) => void
  resetFilters: () => void
  resetKeyword: () => void
  resetSort: () => void
  resetAll: () => void
}

type Field = keyof TSearchFilter

type SearchFilterState = {
  filters: TSearchFilter
  count: number
  keyword: string
  sort: RoomSortOption
  isSame: (field: { field: Field; value: unknown }) => boolean
  hasChanged: (field: Field | Field[]) => boolean
  actions: SearchFilterAction
}

export const defaultFilters: TSearchFilter = {
  startDate: '',
  endDate: '',
  minUsingFee: null,
  maxUsingFee: null,
  roomCounts: [],
  propertyTypes: [],
  pyeongSizes: [],
  floors: [],
  popularOptions: [],
  basicOptions: [],
  additionalOptions: [],
  roomDiscounts: [],
}

export const useSearchFilterStore = create<SearchFilterState>()(
  subscribeWithSelector((set, get) => ({
    filters: defaultFilters,
    count: 0,
    keyword: '',
    sort: RoomSortOption.POPULAR,
    isSame: (field: { field: Field; value: unknown }) => {
      return isEqual(get().filters[field.field], field.value)
    },
    hasChanged: (field: Field | Field[]) => {
      const compareValues = (value: unknown, updatedValue: unknown) => {
        if (Array.isArray(value) && Array.isArray(updatedValue)) {
          return !isEqual([...value].sort(), [...updatedValue].sort())
        }

        return !isEqual(value, updatedValue)
      }

      if (Array.isArray(field)) {
        return field.some(key =>
          compareValues(get().filters[key], defaultFilters[key]),
        )
      }

      return compareValues(defaultFilters[field], get().filters[field])
    },
    actions: {
      _updateCount: (filters: Partial<TSearchFilter>) => {
        const updatedFilters = { ...get().filters, ...filters }
        let count = 0

        const isChanged = (key: keyof TSearchFilter, value: unknown) =>
          !isEqual(value, defaultFilters[key])

        const excludedKeys = [
          'minUsingFee',
          'maxUsingFee',
          'startDate',
          'endDate',
        ]

        Object.keys(defaultFilters).forEach(key => {
          if (excludedKeys.includes(key)) return

          const filter = key as keyof TSearchFilter
          const value = updatedFilters[filter]

          if (isChanged(filter, value)) {
            count += Array.isArray(value) ? value.length : 1
          }
        })

        if (
          isChanged('minUsingFee', updatedFilters.minUsingFee) ||
          isChanged('maxUsingFee', updatedFilters.maxUsingFee)
        ) {
          count += 1
        }

        return count
      },
      update: filters => {
        const updatedCount = get().actions._updateCount(filters)

        set({
          filters: { ...get().filters, ...filters },
          count: updatedCount,
        })
      },
      updateKeyword: (keyword: string) => {
        set({ keyword })
      },
      updateSort: (sort: RoomSortOption) => {
        set({ sort })
      },
      reset: (fields: Field | Field[]) => {
        const resetFilters: Partial<TSearchFilter> = {}

        if (Array.isArray(fields)) {
          fields.forEach(field => {
            resetFilters[field] = defaultFilters[field] as never
          })
        } else {
          resetFilters[fields] = defaultFilters[fields] as never
        }

        get().actions.update(resetFilters)
      },
      resetFilters: () => {
        const { startDate, endDate, ...filters } = defaultFilters
        set({ filters: { ...get().filters, ...filters }, count: 0 })
      },
      resetKeyword: () => {
        set({ keyword: '' })
      },
      resetSort: () => {
        set({ sort: RoomSortOption.POPULAR })
      },
      resetAll: () => {
        set({
          filters: defaultFilters,
          count: 0,
          keyword: '',
          sort: RoomSortOption.POPULAR,
        })
      },
    },
  })),
)
